import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Users, MessageSquare, Download, RefreshCw } from "lucide-react";
import { getMe, logout, getAdminStats, getAdminWaitlist, getAdminContacts } from "@/lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const toCsv = (rows, headers) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = headers.map((h) => esc(h.label)).join(",");
  const body = rows.map((r) => headers.map((h) => esc(r[h.key])).join(",")).join("\n");
  return head + "\n" + body;
};

const download = (name, content) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("waitlist");
  const [stats, setStats] = useState({ waitlist: 0, contacts: 0 });
  const [waitlist, setWaitlist] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const [s, w, c] = await Promise.all([getAdminStats(), getAdminWaitlist(), getAdminContacts()]);
      setStats(s);
      setWaitlist(w);
      setContacts(c);
    } catch {
      toast.error("Could not load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await getMe();
        setUser(me);
        await loadData();
      } catch {
        navigate("/admin/login");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doLogout = async () => {
    try {
      await logout();
    } catch {}
    navigate("/admin/login");
  };

  const exportCsv = () => {
    if (tab === "waitlist") {
      download(
        "trainova-waitlist.csv",
        toCsv(waitlist, [
          { key: "name", label: "Name" },
          { key: "email", label: "Email" },
          { key: "number", label: "Number" },
          { key: "city", label: "City" },
          { key: "goal", label: "Goal" },
          { key: "created_at", label: "Submitted" },
        ])
      );
    } else {
      download(
        "trainova-contacts.csv",
        toCsv(contacts, [
          { key: "first_name", label: "First Name" },
          { key: "last_name", label: "Last Name" },
          { key: "email", label: "Email" },
          { key: "number", label: "Number" },
          { key: "message", label: "Message" },
          { key: "created_at", label: "Submitted" },
        ])
      );
    }
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] text-white">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-teal border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <img src="/trainova-logo.png" alt="Trainova" className="h-8 w-8 rounded-sm object-cover" />
            <span className="font-condensed text-xl tracking-widest">TRAINOVA ADMIN</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-zinc-400 sm:block">{user.email}</span>
            <button
              onClick={doLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm text-zinc-200 transition-colors hover:border-teal hover:text-teal"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 py-10">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl uppercase leading-none md:text-5xl">Submissions</h1>
            <p className="mt-2 text-zinc-400">All waitlist signups and contact messages.</p>
          </div>
          <button
            onClick={loadData}
            data-testid="admin-refresh-btn"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm text-zinc-200 transition-colors hover:border-teal hover:text-teal"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {/* Stat cards */}
        <div className="mb-8 grid gap-4 sm:grid-cols-2">
          <div className="border border-white/10 bg-[#101010] p-6" data-testid="stat-waitlist">
            <div className="flex items-center gap-3 text-teal">
              <Users size={20} />
              <span className="text-xs uppercase tracking-[0.2em] text-zinc-400">Waitlist signups</span>
            </div>
            <div className="mt-3 font-display text-5xl">{stats.waitlist}</div>
          </div>
          <div className="border border-white/10 bg-[#101010] p-6" data-testid="stat-contacts">
            <div className="flex items-center gap-3 text-teal">
              <MessageSquare size={20} />
              <span className="text-xs uppercase tracking-[0.2em] text-zinc-400">Contact messages</span>
            </div>
            <div className="mt-3 font-display text-5xl">{stats.contacts}</div>
          </div>
        </div>

        {/* Tabs + export */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2">
            <button
              onClick={() => setTab("waitlist")}
              data-testid="tab-waitlist"
              className={`px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors ${
                tab === "waitlist" ? "bg-teal text-black" : "border border-white/15 text-zinc-300 hover:border-teal"
              }`}
            >
              Waitlist ({waitlist.length})
            </button>
            <button
              onClick={() => setTab("contacts")}
              data-testid="tab-contacts"
              className={`px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors ${
                tab === "contacts" ? "bg-teal text-black" : "border border-white/15 text-zinc-300 hover:border-teal"
              }`}
            >
              Messages ({contacts.length})
            </button>
          </div>
          <button
            onClick={exportCsv}
            data-testid="admin-export-btn"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm text-zinc-200 transition-colors hover:border-teal hover:text-teal"
          >
            <Download size={16} /> Export CSV
          </button>
        </div>

        {/* Tables */}
        <div className="overflow-x-auto border border-white/10">
          {tab === "waitlist" ? (
            <table className="w-full min-w-[640px] text-left text-sm" data-testid="waitlist-table">
              <thead className="bg-[#141414] text-xs uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Number</th>
                  <th className="px-4 py-3">City</th>
                  <th className="px-4 py-3">Goal</th>
                  <th className="px-4 py-3">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {waitlist.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">No signups yet.</td></tr>
                )}
                {waitlist.map((r, i) => (
                  <tr key={i} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 font-medium">{r.name}</td>
                    <td className="px-4 py-3 text-zinc-300">{r.email}</td>
                    <td className="px-4 py-3 text-zinc-400">{r.number || "—"}</td>
                    <td className="px-4 py-3 text-zinc-400">{r.city || "—"}</td>
                    <td className="px-4 py-3 text-zinc-400">{r.goal || "—"}</td>
                    <td className="px-4 py-3 text-zinc-500">{fmtDate(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full min-w-[720px] text-left text-sm" data-testid="contacts-table">
              <thead className="bg-[#141414] text-xs uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Number</th>
                  <th className="px-4 py-3">Message</th>
                  <th className="px-4 py-3">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {contacts.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500">No messages yet.</td></tr>
                )}
                {contacts.map((r, i) => (
                  <tr key={i} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 font-medium">{`${r.first_name} ${r.last_name || ""}`.trim()}</td>
                    <td className="px-4 py-3 text-zinc-300">{r.email}</td>
                    <td className="px-4 py-3 text-zinc-400">{r.number || "—"}</td>
                    <td className="max-w-md px-4 py-3 text-zinc-400">{r.message}</td>
                    <td className="px-4 py-3 text-zinc-500">{fmtDate(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
